/*
 * storefront-attribution.js — Tymewear referral attribution shim.
 *
 * Canonical source — this file is the single source of truth. The
 * theme engineer copies it verbatim into the Shopify theme repo at
 * `assets/storefront-attribution.js` and references it from
 * `theme.liquid` via:
 *   <script src="{{ 'storefront-attribution.js' | asset_url }}" defer></script>
 * Place the <script> tag at end-of-<body>; Shopify CDN does not
 * honor `defer` consistently across themes.
 *
 * Runs in the customer browser on tymewear.com (Shopify-hosted
 * theme), NEVER inside the Django backend. Backend deps:
 *   - Story 2.5: POST https://api.tymewear.com/v2/api/referrals/validate
 *   - Story 2.7: CORS allowlist for the tymewear.com origin (no creds)
 *   - Story 2.8: orders/create handler reads note_attributes._referrer_code
 *   - Gift display: a visible line-item property on the membership so
 *     checkout (Grow plan) shows "🎁 GIFT: 3 MONTHS FREE INCLUDED ✅".
 *     Names starting with _ are hidden from the buyer. Do not rename
 *     this property to _gift.
 *
 * Cookie wire format: `tw_ref` = btoa(JSON.stringify({code, sig, exp})).
 * This is NOT an RFC 7519 JWT — the embedded `sig` IS the HMAC-SHA256
 * issued by the backend's signing.py (architecture.md §483, §617). The
 * browser NEVER sees SHARE_LINK_SECRET. Any cookie consumer that needs
 * to re-validate posts the unpacked triple back to /v2/api/referrals/
 * validate — the same uniform-negative endpoint. Do NOT invent a
 * parallel HMAC primitive here.
 *
 * Vanilla ES2017, no build step, IIFE-wrapped. `var` is used for
 * maximum compatibility — Shopify themes target a long tail of
 * browser versions. NEVER throw from this IIFE: an uncaught exception
 * in a Shopify theme breaks page render for ALL visitors, not only
 * referees. Every fetch path is wrapped in `.catch` + silent no-op.
 *
 * Feature-flag transparency: when REFERRALS_ATTRIBUTION_ENABLED is
 * False server-side, /validate returns {"valid": false} uniformly;
 * the shim short-circuits per AC3. No client-side flag check needed.
 */

(function () {
  'use strict';

  var VALIDATE_URL = 'https://api.tymewear.com/v2/api/referrals/validate';
  var CART_UPDATE_URL = '/cart/update.js';
  var COOKIE_NAME = 'tw_ref';
  var COOKIE_MAX_AGE = 2592000; // 30 days
  var BANNER_ELEMENT_ID = 'tw-referral-banner';
  var GIFT_PROPERTY_NAME = '🎁 GIFT';
  var GIFT_PROPERTY_VALUE = '3 MONTHS FREE INCLUDED ✅';
  var MEMBERSHIP_VARIANT_IDS = {
    '40965980651555': true,
    '40751038365731': true,
    '41982662639651': true
  };

  function cartEndpoint(file) {
    var root =
      (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) ||
      '/';
    return String(root).replace(/\/?$/, '/') + file;
  }

  function readReferral() {
    try {
      var raw = readCookie(COOKIE_NAME);
      if (!raw) return null;
      var decoded = JSON.parse(atob(raw));
      if (!decoded || typeof decoded.code !== 'string' || !decoded.code) return null;
      var exp = Number(decoded.exp);
      if (Number.isFinite(exp) && exp > 0) {
        var expMs = exp > 1e12 ? exp : exp * 1000;
        if (expMs <= Date.now()) return null;
      }
      return decoded;
    } catch (e) {
      return null;
    }
  }

  function giftIsActive() {
    return !!readReferral();
  }

  function isMembershipLine(item) {
    if (!item) return false;
    if (item.selling_plan_allocation) return true;
    var variantId = String(item.variant_id || item.id || '');
    return !!MEMBERSHIP_VARIANT_IDS[variantId];
  }

  function applyGiftToPayload(payload) {
    if (!payload || !giftIsActive()) return payload;
    var source = payload.properties && payload.properties !== false ? payload.properties : {};
    var next = {};
    Object.keys(source).forEach(function (key) {
      next[key] = source[key];
    });
    next[GIFT_PROPERTY_NAME] = GIFT_PROPERTY_VALUE;
    payload.properties = next;
    return payload;
  }

  function syncGiftProperty() {
    if (!giftIsActive()) return Promise.resolve();
    return fetch(cartEndpoint('cart.js'), { credentials: 'same-origin' })
      .then(function (res) {
        return res.ok ? res.json() : null;
      })
      .then(function (cart) {
        var items = (cart && cart.items) || [];
        var chain = Promise.resolve();
        items.forEach(function (item) {
          if (!isMembershipLine(item) || !item.key) return;
          var existing = item.properties || {};
          if (
            existing[GIFT_PROPERTY_NAME] === GIFT_PROPERTY_VALUE &&
            !existing.Gift &&
            !existing.GIFT
          ) return;
          chain = chain.then(function () {
            var props = {};
            Object.keys(existing).forEach(function (key) {
              if (key === 'Gift' || key === 'GIFT' || key === GIFT_PROPERTY_NAME) return;
              if (existing[key] != null && existing[key] !== '') props[key] = existing[key];
            });
            props[GIFT_PROPERTY_NAME] = GIFT_PROPERTY_VALUE;
            return fetch(cartEndpoint('cart/change.js'), {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              credentials: 'same-origin',
              body: JSON.stringify({
                id: item.key,
                quantity: item.quantity,
                properties: props
              })
            }).catch(function () {});
          });
        });
        return chain;
      })
      .catch(function () {});
  }

  function readCookie(name) {
    var m = document.cookie.match(
      new RegExp('(?:^|; )' + name + '=([^;]*)')
    );
    return m ? decodeURIComponent(m[1]) : null;
  }

  function attachCartAttribute(code) {
    fetch(CART_UPDATE_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ attributes: { _referrer_code: code } })
    }).catch(function () {
      // Silent — retried on next pageload.
    });
  }

  // Cart-attribute reattach runs on EVERY pageload when the cookie is
  // present. A referee may land via the share link, browse for days
  // (cookie persists 30 days), then add to cart — without this re-
  // attach, a stale cart that pre-dated the click never picks up
  // _referrer_code at checkout. Shopify accepts the same attribute
  // being set repeatedly (idempotent).
  var existingCookie = readCookie(COOKIE_NAME);
  if (existingCookie) {
    try {
      var decoded = JSON.parse(atob(existingCookie));
      if (decoded && typeof decoded.code === 'string' && decoded.code) {
        attachCartAttribute(decoded.code);
        syncGiftProperty();
      }
    } catch (e) {
      // base64 / JSON tampering — silent no-op.
    }
  }

  window.TymewearReferralGift = {
    PROPERTY_NAME: GIFT_PROPERTY_NAME,
    PROPERTY_VALUE: GIFT_PROPERTY_VALUE,
    isActive: giftIsActive,
    applyToPayload: applyGiftToPayload,
    syncCart: syncGiftProperty
  };

  var params = new URLSearchParams(window.location.search);
  var code = params.get('ref');
  var sig = params.get('sig');
  var exp = parseInt(params.get('exp') || '', 10);
  if (!code || !sig || !Number.isFinite(exp) || exp <= 0) return;

  fetch(VALIDATE_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'omit',
    body: JSON.stringify({ code: code, sig: sig, exp: exp })
  })
    .then(function (r) {
      return r.ok ? r.json() : null;
    })
    .then(function (data) {
      if (!data || data.valid !== true) return;
      var firstName = data.referrer_first_name || '';

      var payload = btoa(
        JSON.stringify({ code: code, sig: sig, exp: exp })
      );
      // Host-aware Domain: a `.tymewear.com` cookie is silently rejected by
      // the browser on any other host (e.g. the *.myshopify.com dev-store),
      // killing the every-pageload reattach path. Only scope to the parent
      // domain on real tymewear.com hosts; everywhere else write a host-only
      // cookie (omit Domain) so the cookie actually persists.
      var domainAttr = /(^|\.)tymewear\.com$/.test(window.location.hostname)
        ? '; Domain=.tymewear.com'
        : '';
      document.cookie =
        COOKIE_NAME + '=' + payload +
        domainAttr +
        '; Path=/' +
        '; Max-Age=' + COOKIE_MAX_AGE +
        '; SameSite=Lax' +
        '; Secure';

      var el = document.getElementById(BANNER_ELEMENT_ID);
      if (el) {
        var name = firstName || 'Someone';
        // Banner is a click-through CTA to the 3-bundle package page.
        // Built via createElement + textContent (NOT innerHTML) so the
        // server-supplied name can never inject markup — MyUser.first_name
        // is max_length=50 server-side but is still never trusted as HTML.
        //
        // Carry ?ref/sig/exp on the CTA so the package page can re-run
        // /validate and reattach _referrer_code even when the cookie is
        // unavailable (host-only cookie + cross-page nav). encodeURIComponent
        // each value — sig is base64url and may contain reserved chars.
        var refQuery =
          'ref=' + encodeURIComponent(code) +
          '&sig=' + encodeURIComponent(sig) +
          '&exp=' + encodeURIComponent(String(exp));
        var link = document.createElement('a');
        var root =
          (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) ||
          '/';
        link.href =
          String(root).replace(/\/?$/, '/') + 'pages/vitalpro-strap?' + refQuery;
        link.style.color = 'inherit';
        link.style.textDecoration = 'underline';
        link.textContent =
          name + ' gave you 3 months free! Select your package to claim the gift';
        el.textContent = '';
        el.appendChild(link);
        el.style.display = '';
      }

      attachCartAttribute(code);
      syncGiftProperty();
    })
    .catch(function () {
      // Network error / CORS preflight failure — silent no-op.
    });
})();
